import * as Sentry from "@sentry/react";
import React from 'react';

if (process.env.NODE_ENV !== 'development') {
  const isProduction = process.env.NODE_ENV === 'production';
  
  Sentry.init({
    dsn: "https://ab3db9a4c5d5dbd88981ecbe7ee71e91@o4508850731614208.ingest.de.sentry.io/4508850783584336",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    environment: process.env.SENTRY_ENVIRONMENT || process.env.NODE_ENV || 'production',
    beforeSend: (event) => {
      // Ignore specific user agents
      const userAgent = event.request?.headers?.['User-Agent'];
      const ignoredUserAgents = ["YisouSpider", "HeadlessChrome"];
      if (userAgent && ignoredUserAgents.some(agent => userAgent.includes(agent))) {
        return null;
      }
      
      // Ignore URL paths including '/vb-admin'
      const url = event.request?.url;
      if (url && (url.includes('/vb-admin/') || url.match(/\/vb-admin($|\/).*/))) {
        return null;
      }
      
      return event;
    },
    // Tracing
    tracesSampler: (samplingContext) => {
      const { name } = samplingContext;
    
      // Sample all error-related transactions
      if (name?.includes('error') || name?.includes('exception')) {
        return isProduction ? 0.6 : 0.10;
      }
    
      return isProduction ? 0.20 : 0.10; 
    },
    
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
    
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: isProduction ? 0.3 : 0.1,
  });
}

// HOC to wrap components with Sentry user context
const withSentryUserData = (WrappedComponent) => {
  return class extends React.Component {
    componentDidMount() {
      if (Window.user_data && Window.user_data.isLogged) {
        let userTag = "authenticated";
        if (Window.user_data.isSuperUser || Window.user_data.isAdmin || Window.user_data.isExternalAdmin) {
          userTag = "admin";
        }
        Sentry.setTag("user_type", userTag);

        let role = 'user';
        if (Window.user_data.isSuperUser) {
          role = 'super_admin';
        } else if (Window.user_data.isAdmin) {
          role = Window.user_data.isExternalAdmin ? 'external_admin' : 'internal_admin';
        }
       
        Sentry.setUser({
          accountId: Window.user_data.loggedInUserID.toString(),
          permission_role: role,
        });
      } else {
        Sentry.setTag("user_type", "anonymous");
        Sentry.setUser(null);
      }
    }

    render() {
      return <WrappedComponent {...this.props} />;
    }
  };
};

// Wrap LoginPoup
const wrapWithLoginPopup = (Component) => {
  return (props) => <LoginWrapper>
    <Component {...props} />
  </LoginWrapper>
}

import VBErrorBoundary from "@virkesborsen/vb-js-commons/lib/components/vbErrorBoundaryHandler";

import ComplementInfo from "./login/complementinfo.js";

import PriceMap from "./treebula/price-map";

import Error404 from "./vb-django/pages/Error404";
import MinSkog from "./vb-django/pages/MinSkog"
 
// Other pages
import FAQPage from "./vb-django/pages/FAQPage";
import ForestCataloguePage from "./vb-django/pages/ForestCatalogue";
import ContactSellerPage from "./vb-django/pages/ContactSeller";
import ProfileEditPage from "./vb-django/pages/ProfileEdit";
import SBPToPdf from "./vb-django/pages/SBPToPdf";
import PricingPage from "./vb-django/components/minskog/aragon/PricingPage.js";

// Auctions
import AuctionDetailsPage from "./vb-django/pages/auctions/AuctionDetails.js";
import AuctionsCreatePage from "./vb-django/pages/auctions/AuctionsCreate.js";
import Auctions from "./vb-django/pages/auctions/Auctions";

// Naturehub
import NHListings from "./vb-django/pages/naturehub/nhListings.js";
import NHDetails from "./vb-django/pages/naturehub/nhDetails.js";
import NHCreate from "./vb-django/pages/naturehub/nhCreate.js";

//Map service layer upload
import UserMapLayerUpload from "./vb-django/pages/UserMapLayerUpload.js";

// Typeform
import Typeform from "./vb-django/pages/TypeformPage/index.js";
import BookDemoForm from "./vb-django/pages/TypeformPage/BookDemoForm.js";

// Puck
import Editor from "./vb-django/components/editor/Editor";
import PuckPageView from "./vb-django/components/editor/PuckPageView";

// Form Modal
import LoginFormModal from "./vb-django/forms/LoginFormModal.js"

import LoginWrapper from "./vb-django/components/login/LoginWrapper";

// Pricecomparison
import PriceComparison from "./vb-django/components/pricecomparisonold/PriceComparison.js";
import UnderConstruction from "./vb-django/pages/UnderConstruction.js";

require('es6-promise').polyfill();
require('isomorphic-fetch');
require('./polyfills.js');

// Export components wrapped with Sentry user context for logging purposes
export default {
  "VBErrorBoundary": withSentryUserData(VBErrorBoundary),

  "ComplementInfo": wrapWithLoginPopup(withSentryUserData(ComplementInfo)),
  "PriceMap": wrapWithLoginPopup(withSentryUserData(PriceMap)),
  "Error404": wrapWithLoginPopup(withSentryUserData(Error404)),
  "MinSkog": wrapWithLoginPopup(withSentryUserData(MinSkog)),

  // Here comes the new stuff
  "FAQPage": wrapWithLoginPopup(withSentryUserData(FAQPage)),
  "ForestCataloguePage": wrapWithLoginPopup(withSentryUserData(ForestCataloguePage)),
  "ContactSellerPage": wrapWithLoginPopup(withSentryUserData(ContactSellerPage)),
  "ProfileEditPage": wrapWithLoginPopup(withSentryUserData(ProfileEditPage)),
  "SBPToPdf": withSentryUserData(SBPToPdf),

  // Nature hub
  "NHListings": wrapWithLoginPopup(withSentryUserData(NHListings)),
  "NHDetails": wrapWithLoginPopup(withSentryUserData(NHDetails)),
  "NHCreate": wrapWithLoginPopup(withSentryUserData(NHCreate)),

  //Upload map layer
  "UserMapLayerUpload": wrapWithLoginPopup(UserMapLayerUpload),


  // Typeform
  "Typeform": wrapWithLoginPopup(withSentryUserData(Typeform)),
  "BookDemoForm": wrapWithLoginPopup(withSentryUserData(BookDemoForm)),
  
  // Puck
  "Editor": wrapWithLoginPopup(withSentryUserData(Editor)),
  "PuckPageView": wrapWithLoginPopup(withSentryUserData(PuckPageView)),
  "AuctionDetailsPage": wrapWithLoginPopup(withSentryUserData(AuctionDetailsPage)),
  "AuctionsCreatePage": wrapWithLoginPopup(withSentryUserData(AuctionsCreatePage)),
  "Auctions": wrapWithLoginPopup(withSentryUserData(Auctions)),

  // Form Modal
  "LoginFormModal": withSentryUserData(LoginFormModal),
  // Pricecomparison
  "PriceComparison": wrapWithLoginPopup(withSentryUserData(PriceComparison)),

  "UnderConstruction": withSentryUserData(UnderConstruction),

  "PricingPageLoggedOut":wrapWithLoginPopup(withSentryUserData(PricingPage)),
}